import { DownloadSharp } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";

import { exportAdminOrders } from "../../services/order.service";
import type { OrderFilters } from "../../types/order";
import CustomDatePicker from "../CustomDatePicker";

const MAX_EXPORT_DAYS = 366;

type Shortcut = {
  label: string;
  getRange: () => { start: Dayjs; end: Dayjs };
};

const shortcuts: Shortcut[] = [
  {
    label: "Today",
    getRange: () => ({ start: dayjs(), end: dayjs() }),
  },
  {
    label: "Yesterday",
    getRange: () => ({
      start: dayjs().subtract(1, "day"),
      end: dayjs().subtract(1, "day"),
    }),
  },
  {
    label: "Last 7 days",
    getRange: () => ({ start: dayjs().subtract(6, "day"), end: dayjs() }),
  },
  {
    label: "This month",
    getRange: () => ({
      start: dayjs().startOf("month"),
      end: dayjs().endOf("month"),
    }),
  },
  {
    label: "Last month",
    getRange: () => ({
      start: dayjs().subtract(1, "month").startOf("month"),
      end: dayjs().subtract(1, "month").endOf("month"),
    }),
  },
];

interface OrderCsvExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current list filters (search, statuses, sort) carried into the export. */
  baseFilters?: OrderFilters;
}

const getNestedErrorValue = (value: unknown): string | null => {
  if (!value || typeof value !== "string") return null;

  const jsonStart = value.indexOf("{");
  if (jsonStart === -1) return value;

  try {
    const parsed = JSON.parse(value.slice(jsonStart));
    return parsed?.value || parsed?.message || value;
  } catch {
    return value;
  }
};

const getExportErrorMessage = async (error: any) => {
  let data = error?.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  const message =
    getNestedErrorValue(data?.value) ||
    getNestedErrorValue(data?.message) ||
    getNestedErrorValue(data) ||
    error?.message;

  return message || "Failed to export orders.";
};

export default function OrderCsvExportDialog({
  open,
  onClose,
  baseFilters,
}: OrderCsvExportDialogProps) {
  const defaultRange = shortcuts[3].getRange();
  const [startDate, setStartDate] = useState<Dayjs | null>(defaultRange.start);
  const [endDate, setEndDate] = useState<Dayjs | null>(defaultRange.end);
  const [selectedShortcut, setSelectedShortcut] = useState("This month");
  const [loading, setLoading] = useState(false);
  const [exportError, setExportError] = useState("");

  const dateError = useMemo(() => {
    if (!startDate || !endDate) return "Please select both start and end date.";
    if (!startDate.isValid() || !endDate.isValid()) {
      return "Please select a valid date range.";
    }
    if (endDate.isBefore(startDate, "day")) {
      return "End date must be after or equal to start date.";
    }
    if (endDate.diff(startDate, "day") + 1 > MAX_EXPORT_DAYS) {
      return `Date range must not exceed ${MAX_EXPORT_DAYS} days.`;
    }
    return "";
  }, [endDate, startDate]);

  const rangeSummary = useMemo(() => {
    if (dateError || !startDate || !endDate) return "";
    const totalDays = endDate.diff(startDate, "day") + 1;
    return `${startDate.format("DD MMM YYYY")} – ${endDate.format(
      "DD MMM YYYY",
    )} (${totalDays} ${totalDays === 1 ? "day" : "days"})`;
  }, [dateError, endDate, startDate]);

  const onShortcut = (shortcut: Shortcut) => {
    const range = shortcut.getRange();
    setStartDate(range.start);
    setEndDate(range.end);
    setSelectedShortcut(shortcut.label);
    setExportError("");
  };

  const onStartDateChange = (value: Dayjs | null) => {
    const nextDate = value ? dayjs(value) : null;
    setStartDate(nextDate);
    setSelectedShortcut("Custom");
    setExportError("");

    if (nextDate && endDate?.isBefore(nextDate, "day")) {
      setEndDate(nextDate);
    }
  };

  const onEndDateChange = (value: Dayjs | null) => {
    setEndDate(value ? dayjs(value) : null);
    setSelectedShortcut("Custom");
    setExportError("");
  };

  const onDownload = async () => {
    if (dateError || !startDate || !endDate) return;

    setExportError("");
    setLoading(true);
    try {
      await exportAdminOrders({
        search: baseFilters?.search || undefined,
        sort: baseFilters?.sort,
        statuses: baseFilters?.statuses,
        order_type: baseFilters?.order_type,
        time_from: startDate.startOf("day").toISOString(),
        time_to: endDate.endOf("day").toISOString(),
      });
      onClose();
    } catch (error) {
      console.error("Error exporting orders:", error);
      setExportError(await getExportErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Export orders</DialogTitle>
      <DialogContent sx={{ px: 3, py: 1 }}>
        <Stack spacing={2}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {shortcuts.map((shortcut) => (
              <Button
                key={shortcut.label}
                type="button"
                size="small"
                variant={
                  selectedShortcut === shortcut.label ? "contained" : "outlined"
                }
                onClick={() => onShortcut(shortcut)}
                sx={{ minWidth: "auto" }}
              >
                {shortcut.label}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" gap={1.25}>
            <CustomDatePicker
              fullWidth
              size="small"
              label="Start"
              value={startDate}
              maxDate={endDate ?? undefined}
              onChange={onStartDateChange}
            />
            <CustomDatePicker
              fullWidth
              size="small"
              label="End"
              value={endDate}
              minDate={startDate ?? undefined}
              onChange={onEndDateChange}
            />
          </Stack>

          <Box minHeight={36}>
            {dateError || exportError ? (
              <Alert severity="error" sx={{ py: 0 }}>
                {dateError || exportError}
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {rangeSummary}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button type="button" size="small" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          size="small"
          variant="contained"
          startIcon={<DownloadSharp className="text-white" />}
          onClick={onDownload}
          disabled={!!dateError}
          loading={loading}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
